import { useMemo, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";

import { api } from "../api/client";
import type { Node, Trunk } from "../api/types";
import { failureMessage, useResource } from "../api/useResource";
import { usePagedResource } from "../api/usePagedResource";
import {
  ActionError, Badge, Button, Field, Modal, PageHeader, inputClass,
} from "../components/ui";
import { DataTable } from "../components/data-table";

// Troncales SIP. Vista de plataforma (superadmin).
//
// Una troncal es la salida y la entrada al mundo telefónico: sin una activa no
// hay llamadas en ninguna dirección. Los límites que se editan aquí no son
// decorativos — el despachador los respeta antes de originar (ADR-012), y
// ponerlos por encima de lo que el carrier acepta se traduce en llamadas
// rechazadas por el otro lado, no en más capacidad.
//
// Lo que esta vista **no** muestra son las credenciales del endpoint PJSIP:
// viven en la configuración del nodo, el endpoint no las devuelve y §8 prohíbe
// exponerlas.

export function Trunks() {
  const trunks = usePagedResource<Trunk>("/api/v1/trunks");
  const nodes = useResource<Node[]>("/api/v1/nodes");

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Trunk | null>(null);
  const [actionError] = useState<string | null>(null);

  const nodeName = (id: string) => nodes.data?.find((n) => n.id === id)?.name ?? id;

  // Las columnas de troncales: la misma tabla que Cuentas, otra configuración.
  // Nada de esto entra en el DataTable, que no sabe qué es una troncal.
  const columnas = useMemo<ColumnDef<Trunk, any>[]>(
    () => [
      {
        id: "name",
        accessorFn: (trunk) => `${trunk.name} ${trunk.endpoint_name}`,
        header: "Troncal",
        meta: { label: "Troncal" },
        cell: ({ row }) => (
          <>
            <div className="font-medium text-slate-100">{row.original.name}</div>
            <div className="font-mono text-xs text-slate-500">{row.original.endpoint_name}</div>
          </>
        ),
      },
      {
        id: "node",
        accessorFn: (trunk) => trunk.node_id,
        header: "Nodo",
        meta: { label: "Nodo", hideOnMobile: true },
        cell: ({ row }) => <span className="text-slate-300">{nodeName(row.original.node_id)}</span>,
      },
      {
        id: "direction",
        accessorKey: "direction",
        header: "Dirección",
        meta: { label: "Dirección" },
        cell: ({ row }) => (
          <span className="text-slate-300">{DIRECTION_LABELS[row.original.direction]}</span>
        ),
      },
      {
        id: "max_cps",
        accessorKey: "max_cps",
        header: "CPS",
        // Los números a la derecha: así se comparan de un vistazo entre filas.
        meta: { label: "CPS", align: "right", width: "6rem" },
      },
      {
        id: "max_concurrent",
        accessorKey: "max_concurrent",
        header: "Concurrencia",
        meta: { label: "Concurrencia", align: "right", width: "8rem" },
      },
      {
        id: "marcacion",
        accessorFn: (trunk) => formatoDeMarcacion(trunk),
        header: "Marcación",
        meta: { label: "Marcación", hideOnMobile: true },
        cell: ({ row }) => (
          <span className="font-mono text-xs text-slate-400">{formatoDeMarcacion(row.original)}</span>
        ),
      },
      {
        id: "status",
        accessorKey: "status",
        header: "Estado",
        meta: { label: "Estado", width: "8rem" },
        cell: ({ row }) => (
          <Badge tone={row.original.status === "active" ? "good" : "neutral"}>
            {row.original.status === "active" ? "Activa" : "Deshabilitada"}
          </Badge>
        ),
      },
    ],
    [nodes.data],
  );

  return (
    <div>
      <PageHeader
        title="Troncales SIP"
        description="Por dónde salen y entran las llamadas. Los límites de CPS y concurrencia se aplican antes de originar: el despachador espera en lugar de desbordar al carrier."
        action={!creating && <Button onClick={() => setCreating(true)}>Nueva troncal</Button>}
      />

      {actionError !== null && <ActionError message={actionError} />}

      {creating && (
        <TrunkForm
          nodes={nodes}
          onCancel={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            void trunks.reload(true);
          }}
        />
      )}

      {editing !== null && (
        <TrunkForm
          trunk={editing}
          nodes={nodes}
          onCancel={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void trunks.reload(true);
          }}
        />
      )}

      <DataTable<Trunk>
        data={trunks.data}
        columns={columnas}
        getRowId={(trunk) => trunk.id}
        loading={trunks.loading}
        error={trunks.error}
        onRetry={() => void trunks.reload(false)}
        caption="Troncales SIP de la plataforma"
        empty={{
          title: "Sin troncales",
          description: "Sin una troncal activa no se puede originar ni recibir ninguna llamada.",
        }}
        search={{ placeholder: "Buscar por nombre o endpoint…" }}
        filters={[
          {
            columnId: "status",
            label: "Estado",
            options: [
              { value: "active", label: "Activa" },
              { value: "disabled", label: "Deshabilitada" },
            ],
          },
          {
            columnId: "direction",
            label: "Dirección",
            options: [
              { value: "both", label: "Entrante y saliente" },
              { value: "inbound", label: "Entrante" },
              { value: "outbound", label: "Saliente" },
            ],
          },
        ]}
        rowActions={[{ label: "Editar", onSelect: (trunk) => setEditing(trunk) }]}
        hideableColumns
        urlStateKey="troncales"
        pagination={{
          pageSize: trunks.enEstaPagina,
          hasNext: trunks.hayMas,
          hasPrevious: trunks.hayAnterior,
          onNext: trunks.siguiente,
          onPrevious: trunks.anterior,
        }}
      />
    </div>
  );
}

const DIRECTION_LABELS: Record<Trunk["direction"], string> = {
  inbound: "Entrante",
  outbound: "Saliente",
  both: "Ambas",
};

/**
 * TrunkForm crea o edita una troncal.
 *
 * Al editar, el nombre, el nodo y el endpoint PJSIP se muestran pero no se
 * editan: `PATCH /trunks/{trunkId}` solo acepta dirección, límites y estado
 * (internal/trunks/handlers.go, `updateTrunkRequest`). Ofrecerlos como campos
 * editables sería pintar un formulario que guarda la mitad y no dice nada —
 * mover una troncal de nodo o cambiarle el endpoint tampoco es un cambio de
 * dato, es otra troncal, porque el endpoint PJSIP tiene que existir en el nodo
 * destino.
 */
function TrunkForm({
  trunk,
  nodes,
  onCancel,
  onSaved,
}: {
  trunk?: Trunk;
  nodes: ReturnType<typeof useResource<Node[]>>;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(trunk?.name ?? "");
  const [nodeId, setNodeId] = useState(trunk?.node_id ?? "");
  const [endpointName, setEndpointName] = useState(trunk?.endpoint_name ?? "");
  const [direction, setDirection] = useState<Trunk["direction"]>(trunk?.direction ?? "both");
  const [maxCps, setMaxCps] = useState(String(trunk?.max_cps ?? 10));
  const [maxConcurrent, setMaxConcurrent] = useState(String(trunk?.max_concurrent ?? 30));
  const [stripDigits, setStripDigits] = useState(String(trunk?.dial_strip_digits ?? 0));
  const [dialPrefix, setDialPrefix] = useState(trunk?.dial_prefix ?? "");
  const [status, setStatus] = useState<Trunk["status"]>(trunk?.status ?? "active");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      if (trunk === undefined) {
        await api.post("/api/v1/trunks", {
          name,
          node_id: nodeId,
          endpoint_name: endpointName,
          direction,
          max_cps: Number(maxCps),
          max_concurrent: Number(maxConcurrent),
        });
      } else {
        await api.patch(`/api/v1/trunks/${trunk.id}`, {
          direction,
          max_cps: Number(maxCps),
          max_concurrent: Number(maxConcurrent),
          status,
          dial_strip_digits: Number(stripDigits),
          dial_prefix: dialPrefix.trim(),
        });
      }
      onSaved();
    } catch (err) {
      setError(failureMessage(err, "no se pudo guardar la troncal"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title={trunk === undefined ? "Nueva troncal" : trunk.name}
      description={trunk === undefined
        ? "El nombre y el endpoint no se pueden cambiar después."
        : "Solo estos cuatro campos se pueden modificar."}
      onClose={onCancel}
    >
      <form className="space-y-4" onSubmit={onSubmit}>
        {trunk !== undefined && (
          <div className="rounded-md border border-white/10 bg-white/[0.02] p-3">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
              Formato de marcación
            </p>
            <p className="mt-1 text-xs text-slate-500">
              La plataforma guarda los destinos en E.164 (<code>+525591945409</code>), pero cada
              carrier pide el suyo. Si este espera diez dígitos nacionales, quita 2. Si pide un
              prefijo de salida, ponlo. Un formato que el carrier no entiende responde{" "}
              <code>404 número no asignado</code> en cada llamada.
            </p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <Field label="Dígitos a quitar" hint="Del principio del E.164. 0 = ninguno.">
                <input
                  className={inputClass}
                  type="number"
                  min={0}
                  max={6}
                  value={stripDigits}
                  onChange={(e) => setStripDigits(e.target.value)}
                />
              </Field>
              <Field label="Prefijo" hint="Lo que se antepone. Vacío = nada.">
                <input
                  className={inputClass}
                  placeholder="sin prefijo"
                  value={dialPrefix}
                  onChange={(e) => setDialPrefix(e.target.value)}
                />
              </Field>
            </div>
            <p className="mt-2 text-xs text-slate-500">
              Así se marcaría <code>+525591945409</code>:{" "}
              <code className="text-slate-300">
                {ejemploDeMarcacion(Number(stripDigits) || 0, dialPrefix.trim())}
              </code>
            </p>
          </div>
        )}
        {trunk === undefined ? (
          <Field label="Nombre" hint="Para reconocerla en reportes y alertas.">
            <input
              className={inputClass}
              required
              placeholder="Carrier principal MX"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Field>
        ) : (
          <div className="rounded-md bg-white/[0.04] p-3 text-sm text-slate-400">
            <p className="font-medium text-slate-100">{trunk.name}</p>
            <p className="mt-1">
              Endpoint <code className="text-xs">{trunk.endpoint_name}</code> en el nodo{" "}
              {nodes.data?.find((n) => n.id === trunk.node_id)?.name ?? (
                <span className="font-mono text-xs">{trunk.node_id}</span>
              )}
              .
            </p>
            <p className="mt-1 text-xs">
              Nombre, nodo y endpoint no se editan: el endpoint PJSIP vive en la configuración del
              nodo, así que cambiarlo aquí no cambiaría nada allá. Para eso se crea otra troncal y
              se deshabilita esta.
            </p>
          </div>
        )}

        {trunk === undefined && (
          <Field
            label="Nodo"
            hint="El endpoint PJSIP tiene que existir en ese nodo: la plataforma no lo crea."
          >
            {nodes.error !== null ? (
              <p className="text-sm text-rose-300">No se pudieron cargar los nodos: {nodes.error}</p>
            ) : nodes.loading ? (
              <p className="text-sm text-slate-500">Cargando nodos…</p>
            ) : (nodes.data ?? []).length === 0 ? (
              <p className="text-sm text-amber-300">
                No hay nodos registrados. Una troncal cuelga de un nodo, así que primero hace falta
                uno.
              </p>
            ) : (
              <select
                className={inputClass}
                required
                value={nodeId}
                onChange={(e) => setNodeId(e.target.value)}
              >
                <option value="">Elige uno…</option>
                {(nodes.data ?? []).map((node) => (
                  <option key={node.id} value={node.id}>
                    {node.name}
                  </option>
                ))}
              </select>
            )}
          </Field>
        )}

        {trunk === undefined && (
          <Field
            label="Endpoint PJSIP"
            hint="El nombre tal como está en pjsip.conf del nodo. Si no coincide, la originación falla con CHANUNAVAIL."
          >
            <input
              className={`${inputClass} font-mono`}
              required
              placeholder="carrier-mx"
              value={endpointName}
              onChange={(e) => setEndpointName(e.target.value.trim())}
            />
          </Field>
        )}

        <Field label="Dirección">
          <select
            className={inputClass}
            value={direction}
            onChange={(e) => setDirection(e.target.value as Trunk["direction"])}
          >
            <option value="both">Ambas</option>
            <option value="outbound">Solo saliente</option>
            <option value="inbound">Solo entrante</option>
          </select>
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="CPS máximo"
            hint="Llamadas por segundo que acepta el carrier. Pasarse no da más capacidad: da rechazos."
          >
            <input
              className={inputClass}
              type="number"
              min={1}
              required
              value={maxCps}
              onChange={(e) => setMaxCps(e.target.value)}
            />
          </Field>
          <Field label="Concurrencia máxima" hint="Llamadas simultáneas contratadas con el carrier.">
            <input
              className={inputClass}
              type="number"
              min={1}
              required
              value={maxConcurrent}
              onChange={(e) => setMaxConcurrent(e.target.value)}
            />
          </Field>
        </div>

        {trunk !== undefined && (
          <Field
            label="Estado"
            hint="Deshabilitarla saca la troncal de la selección. Las llamadas en curso no se cuelgan."
          >
            <select
              className={inputClass}
              value={status}
              onChange={(e) => setStatus(e.target.value === "disabled" ? "disabled" : "active")}
            >
              <option value="active">Activa</option>
              <option value="disabled">Deshabilitada</option>
            </select>
          </Field>
        )}

        {error !== null && <p className="text-sm text-rose-300">{error}</p>}

        <div className="flex gap-2">
          <Button type="submit" disabled={submitting}>
            {submitting ? "Guardando…" : trunk === undefined ? "Crear troncal" : "Guardar"}
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/** formatoDeMarcacion resume la transformación en una celda de tabla. */
function formatoDeMarcacion(trunk: Trunk): string {
  if (trunk.dial_strip_digits === 0 && trunk.dial_prefix === "") return "E.164";
  const partes: string[] = [];
  if (trunk.dial_strip_digits > 0) partes.push(`−${trunk.dial_strip_digits}`);
  if (trunk.dial_prefix !== "") partes.push(`+${trunk.dial_prefix}`);
  return partes.join(" ");
}

/**
 * ejemploDeMarcacion enseña el resultado antes de guardar.
 *
 * Es la misma cuenta que hace el backend. Se duplica aquí a sabiendas: ver el
 * número que se va a marcar evita el error de dedo que, en producción, son
 * llamadas fallidas o —peor— llamadas a un número distinto del pedido.
 */
function ejemploDeMarcacion(strip: number, prefix: string): string {
  const e164 = "525591945409";
  const recortado = strip > 0 && strip < e164.length ? e164.slice(strip) : e164;
  return prefix + recortado;
}
